import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { nanoid } from 'nanoid';
import { useCallback, useMemo, useState } from 'react';

import { getModelsAiExtractionContextValue } from '#package-store/package-editor/models-tab/models-ai-extraction-context.js';
import { SetAiExtractionContext } from '#screens/ai-workbench/ai-extraction-context.js';
import { SubmodelsEditor } from '#screens/submodels-editor/submodels-editor.js';
import { useTRPC } from '#src/trpc.js';
import { useAutoSave } from '#utils/use-auto-save/use-auto-save.js';

import type {
	HilSigmaSubModel,
	HilSubModel,
	HilSubModelEditorState,
	HilSubModelMetadata,
} from '@cadstrom/backend/shared';
import type { Dispatch, SetStateAction } from 'react';

type ModelsTabDraft = {
	spiceModels: HilSubModel[];
	subModelEditor: HilSubModelEditorState;
};

export const Route = createFileRoute('/_authorization-gate/package-store/$packageFamilyId/packages/$packageId/models/')(
	{
		staticData: {
			navigation: {
				label: 'Package: Models',
			},
		},
		loader: ({ context: { queryClient, getPackageOptions } }) => {
			void queryClient.prefetchQuery(getPackageOptions);
			const cached = queryClient.getQueryData(getPackageOptions.queryKey);
			return { pkg: cached?.pkg };
		},
		head: ({ loaderData }) => ({
			meta: [
				{
					title:
						(loaderData?.pkg ? loaderData.pkg.manufacturerPartNumber + ': ' : '') +
						'Models • Package Editor • Cadstrom',
				},
			],
		}),
		component: ModelsTab,
	},
);

function ModelsTab() {
	const queryClient = useQueryClient();
	const { getPackageOptions } = Route.useRouteContext();
	const trpc = useTRPC();

	const {
		data: { pkg },
	} = useSuspenseQuery(getPackageOptions);

	const { mutateAsync: updatePackage, isPending: updatePackageIsPending } = useMutation(
		trpc.package.update.mutationOptions({
			onSuccess: (res) => {
				// Merge rather than replace: `isConfidential` is derived from the package family's owner and
				// is not in the mutation response, so overwriting the entry would drop it and hide the badge.
				queryClient.setQueryData(getPackageOptions.queryKey, (previous) =>
					previous === undefined ? undefined : { ...previous, pkg: res.pkg },
				);
				setServerDraft({
					spiceModels: res.pkg.spiceModels,
					subModelEditor: res.pkg.subModelEditor,
				});
			},
		}),
	);

	const initialDraft = useMemo<ModelsTabDraft>(
		() => ({ spiceModels: pkg.spiceModels, subModelEditor: pkg.subModelEditor }),
		[pkg.spiceModels, pkg.subModelEditor],
	);

	const {
		draftState: draft,
		setDraftState: setDraft,
		setServerState: setServerDraft,
		isDirty,
		handleSave,
		saveAreaContainerRef,
	} = useAutoSave({
		initialState: initialDraft,
		mutationKeys: trpc.package.update.mutationKey(),
		saveFn: async (next) =>
			await updatePackage({
				id: pkg.id,
				data: {
					spiceModels: next.spiceModels,
					subModelEditor: next.subModelEditor,
				},
			}),
	});

	const setSpiceModels = useCallback<Dispatch<SetStateAction<HilSubModel[]>>>(
		(updater) => {
			setDraft((prev) => ({
				...prev,
				spiceModels: typeof updater === 'function' ? updater(prev.spiceModels) : updater,
			}));
		},
		[setDraft],
	);

	const setSubModelEditor = useCallback<Dispatch<SetStateAction<HilSubModelEditorState>>>(
		(updater) => {
			setDraft((prev) => ({
				...prev,
				subModelEditor: typeof updater === 'function' ? updater(prev.subModelEditor) : updater,
			}));
		},
		[setDraft],
	);

	// Bumped on every AI workbench replace/append so the canvas remounts even when
	// the LLM keeps the same refdes-style subModelIds (R1, R2, …) but emits
	// new content. Internal canvas updates don't touch this counter.
	const [canvasResetToken, setCanvasResetToken] = useState(0);

	const replaceSigmaSubmodels = useCallback(
		(newSigmaModels: HilSigmaSubModel[]) => {
			// Carry over `metadata.graphPosition` for any submodel whose
			// `subModelId` matches an existing one, so refdes-style ids
			// (R1, R2, …) that the LLM reuses keep the positions the user
			// already dragged them to. New ids fall through to the canvas's
			// auto-placement on remount.
			setSpiceModels((prev) => {
				const oldPositionsById = new Map<string, NonNullable<HilSubModelMetadata['graphPosition']>>();
				for (const m of prev) {
					if (m.type !== 'sigma') {
						continue;
					}
					const pos = m.metadata?.graphPosition;
					if (pos !== undefined) {
						oldPositionsById.set(m.subModelId, pos);
					}
				}
				const merged = newSigmaModels.map((m) => {
					const oldPos = oldPositionsById.get(m.subModelId);
					if (oldPos === undefined) {
						return m;
					}
					return { ...m, metadata: { ...m.metadata, graphPosition: oldPos } };
				});
				return [...prev.filter((m) => m.type !== 'sigma'), ...merged];
			});
			setCanvasResetToken((t) => t + 1);
		},
		[setSpiceModels],
	);

	const appendSigmaSubmodels = useCallback(
		(newSigmaModels: HilSigmaSubModel[]) => {
			// Regenerate any incoming id that collides with one already present
			setSpiceModels((prev) => {
				const taken = new Set(prev.map((m) => m.subModelId));
				const appended = newSigmaModels.map((m) => {
					if (!taken.has(m.subModelId)) {
						taken.add(m.subModelId);
						return m;
					}
					let candidate = `${m.subModelId}-${nanoid(4)}`;
					while (taken.has(candidate)) {
						candidate = `${m.subModelId}-${nanoid(4)}`;
					}
					taken.add(candidate);
					return { ...m, subModelId: candidate };
				});
				return [...prev, ...appended];
			});
			setCanvasResetToken((t) => t + 1);
		},
		[setSpiceModels],
	);

	const {
		data: { sigmaModelDefinitions },
	} = useSuspenseQuery(trpc.misc.getSigmaModelDefinitions.queryOptions({}, { staleTime: Infinity }));

	const extractionContextValue = useMemo(
		() =>
			getModelsAiExtractionContextValue({
				sigmaModelDefinitions,
				componentSignals: pkg.signals,
				replaceSigmaSubmodels,
				appendSigmaSubmodels,
			}),
		[sigmaModelDefinitions, pkg.signals, replaceSigmaSubmodels, appendSigmaSubmodels],
	);

	return (
		<div ref={saveAreaContainerRef} className='flex min-h-0 grow basis-0 flex-col px-3 py-2'>
			<SubmodelsEditor
				spiceModels={draft.spiceModels}
				onChange={setSpiceModels}
				editorState={draft.subModelEditor}
				onEditorStateChange={setSubModelEditor}
				componentSignals={pkg.signals}
				pinMap={pkg.pinMap}
				sigmaModelTypeSet={!!pkg.sigmaModelType}
				canvasResetToken={canvasResetToken}
				showSaveButton={{
					saveDisabled: !isDirty,
					onClickSave: handleSave,
					isSaving: updatePackageIsPending,
				}}
			/>

			<SetAiExtractionContext value={extractionContextValue} />
		</div>
	);
}
